import React, { useEffect, useRef, useState } from 'react';
import { 
  FaGraduationCap, FaUniversity, FaBookOpen, FaChalkboardTeacher,
  FaUsers, FaAward, FaHeart, FaStar, FaCheckCircle, FaArrowRight,
  FaCalendarAlt, FaUserGraduate, FaChalkboard, FaRegBuilding,
  FaQuoteLeft
} from 'react-icons/fa';

const IntroductionSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const highlights = [
    { icon: <FaUniversity />, title: "Affiliated with MGKVP", desc: "Recognized by Mahatma Gandhi Kashi Vidyapith" },
    { icon: <FaBookOpen />, title: "Quality Programs", desc: "B.A. & B.Sc. undergraduate courses" },
    { icon: <FaChalkboardTeacher />, title: "Expert Faculty", desc: "Experienced and dedicated teachers" },
    { icon: <FaUsers />, title: "Student Centric", desc: "Focus on holistic development" }
  ];

  const stats = [
    { icon: <FaCalendarAlt />, value: "2000+", label: "Established" },
    { icon: <FaUserGraduate />, value: "1000+", label: "Students" },
    { icon: <FaChalkboardTeacher />, value: "30+", label: "Faculty" },
    { icon: <FaRegBuilding />, value: "2", label: "Programs" }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-blue-100 via-white to-indigo-50 py-16 lg:py-24 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-3">
              Introduction
            </h2>
            <div className="w-20 sm:w-24 h-1 bg-indigo-500 rounded-full mx-auto"></div>
            <p className="text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4 max-w-2xl mx-auto px-4">
              Know more about our institution's legacy and excellence
            </p>
          </div>

          {/* Main Content - Equal Columns */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-12 lg:mb-16">
            
            {/* Left Side - Main Introduction Text */}
            <div className={`transition-all duration-700 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 lg:-translate-x-16 opacity-0'
            }`} style={{ transitionDelay: '100ms' }}>
              <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-indigo-100 p-2 rounded-xl">
                    <FaGraduationCap className="text-indigo-600 text-xl sm:text-2xl" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                    BNS Mahila Maha Vidyalaya
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <p className="text-gray-600 text-sm leading-relaxed">
                    BNS Mahila Maha Vidyalaya is a distinguished institution dedicated to empowering students through quality higher education. Established with the vision of providing excellent education to women, our college has been a beacon of learning and growth.
                  </p>
                  
                  <p className="text-gray-600 text-sm leading-relaxed">
                        Affiliated with <span className="text-indigo-600 font-semibold">Mahatma Gandhi Kashi Vidyapith (MGKVP)</span>, our college provides an academically enriching environment that nurtures intellectual growth and holistic development. We take pride in our commitment to academic excellence and student success.
                  </p>
                  
                  <p className="text-gray-600 text-sm leading-relaxed">
                    At BNS Mahila Maha Vidyalaya, we offer undergraduate programs in <span className="text-indigo-600 font-semibold">Bachelor of Arts (B.A.)</span> and <span className="text-indigo-600 font-semibold">Bachelor of Science (B.Sc.)</span>, equipping students with the knowledge, skills, and confidence to excel in their chosen fields.
                  </p>
                </div>

                {/* Stats Grid - Moved to Left Side for Balance */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-4 border-t border-gray-100">
                  {stats.map((stat, idx) => (
                    <div key={idx} className="text-center p-2 rounded-lg bg-indigo-50">
                      <div className="text-indigo-500 text-lg sm:text-xl mx-auto mb-1">
                        {stat.icon}
                      </div>
                      <div className="text-indigo-600 text-lg sm:text-xl font-bold">{stat.value}</div>
                      <div className="text-gray-500 text-[10px] sm:text-xs">{stat.label}</div>
                    </div>
                  ))}
                </div>
                
                <div className="bg-indigo-50 rounded-xl p-4 mt-6">
                  <div className="flex items-start gap-3">
                    <FaQuoteLeft className="text-indigo-400 text-base sm:text-lg mt-0.5" />
                    <p className="text-gray-700 text-sm italic">
                      "Join us on a journey of knowledge and transformation at BNS Mahila Maha Vidyalaya!"
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 pt-4">
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className="text-indigo-500 text-xs" />
                    <span className="text-gray-600 text-xs">Quality Education</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className="text-indigo-500 text-xs" />
                    <span className="text-gray-600 text-xs">Expert Faculty</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className="text-indigo-500 text-xs" />
                    <span className="text-gray-600 text-xs">Modern Facilities</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Image and Highlights Cards */}
            <div className={`transition-all duration-700 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 lg:translate-x-16 opacity-0'
            }`} style={{ transitionDelay: '200ms' }}>
              <div className="space-y-5">
                {/* Hero Image Card */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
                    <img 
                      src="collage.png"
                      alt="College Campus"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-white text-sm font-semibold flex items-center gap-2">
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Highlights Cards */}
                {highlights.map((item, idx) => (
                  <div key={idx} className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-all duration-300 hover:translate-x-1 border-l-4 border-indigo-500">
                    <div className="flex items-start gap-4">
                      <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600 text-lg sm:text-xl">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="text-gray-800 font-semibold text-sm sm:text-base mb-1">{item.title}</h4>
                        <p className="text-gray-500 text-xs sm:text-sm">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Gallery Images Row - Additional Images */}
          <div className={`grid grid-cols-3 gap-3 sm:gap-5 mb-12 transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '300ms' }}>
            <div className="relative h-28 sm:h-32 md:h-40 rounded-xl overflow-hidden group cursor-pointer shadow-md">
              <img 
                src="image23.png"
                alt="Classroom"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-2 left-2 right-2 text-white text-[10px] sm:text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                <span>📚</span> Smart Classrooms
              </div>
            </div>
            <div className="relative h-28 sm:h-32 md:h-40 rounded-xl overflow-hidden group cursor-pointer shadow-md">
              <img 
                src="image13.png"
                alt="Library"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-2 left-2 right-2 text-white text-[10px] sm:text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                <span>📖</span> Rich Library
              </div>
            </div>
            <div className="relative h-28 sm:h-32 md:h-40 rounded-xl overflow-hidden group cursor-pointer shadow-md">
              <img 
                src="image20.png"
                alt="Students"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-2 left-2 right-2 text-white text-[10px] sm:text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                <span>👩‍🎓</span> Active Learning
              </div>
            </div>
          </div>

          {/* Key Features Row */}
          <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5 mb-10 transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '400ms' }}>
            <div className="bg-white rounded-xl p-3 sm:p-4 text-center hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg">
              <div className="bg-indigo-100 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mx-auto mb-2">
                <FaStar className="text-indigo-600 text-sm sm:text-base" />
              </div>
              <p className="text-gray-700 text-xs sm:text-sm font-semibold">Quality Education</p>
            </div>
            <div className="bg-white rounded-xl p-3 sm:p-4 text-center hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg">
              <div className="bg-indigo-100 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mx-auto mb-2">
                <FaUsers className="text-indigo-600 text-sm sm:text-base" />
              </div>
              <p className="text-gray-700 text-xs sm:text-sm font-semibold">Student Support</p>
            </div>
            <div className="bg-white rounded-xl p-3 sm:p-4 text-center hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg">
              <div className="bg-indigo-100 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mx-auto mb-2">
                <FaAward className="text-indigo-600 text-sm sm:text-base" />
              </div>
              <p className="text-gray-700 text-xs sm:text-sm font-semibold">Excellence Awards</p>
            </div>
            <div className="bg-white rounded-xl p-3 sm:p-4 text-center hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg">
              <div className="bg-indigo-100 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mx-auto mb-2">
                <FaGraduationCap className="text-indigo-600 text-sm sm:text-base" />
              </div>
              <p className="text-gray-700 text-xs sm:text-sm font-semibold">Placement Support</p>
            </div>
          </div>

          {/* CTA Button */}
          <div className={`text-center transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '500ms' }}>
            <button className="bg-indigo-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base hover:bg-indigo-700 transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto shadow-lg">
              Learn More About Us
              <FaArrowRight className="text-sm" />
            </button>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 sm:mt-12 flex justify-center gap-2">
            <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default IntroductionSection;